import { html, unsafeCSS } from 'lit';
import { query, customElement } from 'lit/decorators.js';
import { styles as specificAccordionStyles } from '../view/view.scss';
import { WEB_COMPONENT_POLICY_PANEL_SANDBOX_MANAGER } from '../../../../../../../webcomponents.constants';
import { translateFn } from '../../../../../../../utils/translator.helper';
import { GzFormFieldWebComponent } from '../../../../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../../../../ui/gzForm/formGroup/form-group';
import { ModuleId, SectionId } from '@nirvana/shared/dtos/types/policies/common';
import { GzPolicyPanelAccordionTemplateWebComponent } from '../../../../../../common/policyPanel/uiSettings/accordionTemplate';
import _ from 'lodash';
import { FormGroupValue } from '../../../../../../../ui/gzForm/formGroup/types/form-group-value';
import { PolicyPanelManager } from '../../../../PolicyPanelManager';
import deepmerge from 'deepmerge';
import { USE_EXT_JS_VERSION } from '../../../../config/config';
import {
  SandboxManagerModuleFormValue,
  SandboxManagerSearchableKeywords,
} from '../../../../utils/uiSettings/sandbox/sandbox.types';
import {
  getSandboxManagerSearchableKeywords,
  MAX_DATA_RETENTION,
  MIN_DATA_RETENTION,
  SANDBOX_MANAGER_DEFAULT_VALUE,
} from '../../../../utils/uiSettings/sandbox/sandbox.values';
import { GzCheckboxWebComponent } from '../../../../../../../ui/gzCheckbox';
import { GzInputNumberWebComponent, InputWidthValues } from '../../../../../../../ui/gzInput';

@customElement(WEB_COMPONENT_POLICY_PANEL_SANDBOX_MANAGER)
export class GzSandboxManagerWebComponent extends GzPolicyPanelAccordionTemplateWebComponent<
  SandboxManagerModuleFormValue,
  SandboxManagerSearchableKeywords
> {
  /**
   * Path for Sandbox Manager section.
   */
  protected path = {
    moduleId: ModuleId.SANDBOX,
    sectionId: SectionId.SANDBOX_MANAGER,
  };

  /**
   * Searchable keywords for Sandbox Manager section.
   */
  protected searchableKeywords = getSandboxManagerSearchableKeywords();

  @query('#data-retention-form-field')
  private dataRetentionFormField: GzFormFieldWebComponent<GzInputNumberWebComponent, number>;

  @query('#persist-samples-form-field')
  private persistSamplesFormField: GzFormFieldWebComponent<GzCheckboxWebComponent, boolean>;

  /**
   * Current accordion styles merged with template styles.
   */
  static get styles() {
    return [...GzPolicyPanelAccordionTemplateWebComponent.styles, unsafeCSS(`${specificAccordionStyles}`)];
  }

  /**
   * Render specific title.
   */
  protected renderTitle() {
    return html`<gz-title
      class="content"
      searchable-label="${this.searchableKeywords[SandboxManagerSearchableKeywords.SandboxManager].label}"
      ><span>${this.searchableKeywords[SandboxManagerSearchableKeywords.SandboxManager].translatedLabel}</span>
    </gz-title>`;
  }

  /**
   * Render specific info text.
   * @protected
   */
  protected renderInfoText() {
    return html` <gz-text fontSize="12" fontWeight="400" class="infoText"
      >${translateFn('Configure Sandbox Analyzer data retention in your environment.')}
    </gz-text>`;
  }

  /**
   * Render specific content.
   */
  protected renderContent() {
    return html`
      <div>
        <gz-form-field id="persist-samples-form-field" class="mt-4">
          <gz-checkbox
            gz-form-input
            searchable-label="${this.searchableKeywords[SandboxManagerSearchableKeywords.PersistSamples].label}"
            label="${this.searchableKeywords[SandboxManagerSearchableKeywords.PersistSamples].translatedLabel}"
          ></gz-checkbox>
          <gz-text fontSize="12" fontWeight="400" class="form-field-info-text"
            >${translateFn(
              'Objects will be retained on the Sandbox Analyzer datastore for the number of days specified below. During this time, the objects will be available for resubmission.'
            )}
          </gz-text>
        </gz-form-field>

        <gz-form-field
          id="data-retention-form-field"
          class="label-mw-250"
          searchable-label="${this.searchableKeywords[SandboxManagerSearchableKeywords.DataRetention].label}"
          label="${this.searchableKeywords[SandboxManagerSearchableKeywords.DataRetention].translatedLabel}"
        >
          <gz-input-number
            gz-form-input
            inputWidth="${InputWidthValues.LARGE}"
            min="${MIN_DATA_RETENTION}"
            max="${MAX_DATA_RETENTION}"
          ></gz-input-number>
        </gz-form-field>

        <gz-info-box
          severity="informative"
          searchable-label="${this.searchableKeywords[SandboxManagerSearchableKeywords.AutomaticDelete].label}"
        >
          ${this.searchableKeywords[SandboxManagerSearchableKeywords.AutomaticDelete].translatedLabel}
        </gz-info-box>
      </div>
    `;
  }

  /**
   * Get data from manager and set missing fields.
   * Fields used in this section:
   * - uiSettings.sandboxManager
   */
  protected getValueFromManager(): SandboxManagerModuleFormValue {
    // get default value
    const defaultValue = {
      uiSettings: {
        [SectionId.SANDBOX_MANAGER]: SANDBOX_MANAGER_DEFAULT_VALUE,
      },
    };

    // get value from manager
    const value = {
      uiSettings: {
        [SectionId.SANDBOX_MANAGER]: PolicyPanelManager.getInstance().getPolicy('uiSettings.sandboxManager'),
      },
    };

    // merge received value with default one
    return deepmerge(defaultValue, value);
  }

  /**
   * Create form group.
   */
  protected createFormGroup() {
    this.formGroup = FormGroup.build({
      uiSettings: {
        sandboxManager: {
          dataRetention: this.dataRetentionFormField,
          persistSamples: this.persistSamplesFormField,
        },
      },
    });
  }

  /**
   * Set form group value.
   */
  protected setFormData() {
    if (!this.valueT) {
      return;
    }

    // set form group value
    this.formGroup.value = {
      uiSettings: {
        sandboxManager: {
          dataRetention: this.valueT.uiSettings.sandboxManager.dataRetention,
          persistSamples: this.valueT.uiSettings.sandboxManager.persistSamples,
        },
      },
    };
  }

  /**
   * Get form group value according to backend format.
   */
  protected getFormGroupValue(): FormGroupValue | null {
    if (!this.formGroup || !this.valueT) {
      return null;
    }

    const value = _.cloneDeep(this.formGroup.value);
    if (USE_EXT_JS_VERSION) {
      return _.get(value, ['uiSettings', 'sandboxManager'], {});
    }

    return value;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    WEB_COMPONENT_POLICY_PANEL_SANDBOX_MANAGER: GzSandboxManagerWebComponent;
  }
}
